// src/components/dashboard/widgets/notes-panel.tsx
import { useCallback, useEffect, useState } from 'react';
import { StickyNote } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';

function useNotepad(venueId: string) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    supabase.from('venues').select('settings').eq('id', venueId).maybeSingle().then(({ data }) => {
      const s = data?.settings as Record<string, string> | null;
      setNote(s?.notepad ?? '');
    });
  }, [venueId]);

  const save = useCallback(async (text: string) => {
    setSaving(true);
    const { data } = await supabase.from('venues').select('settings').eq('id', venueId).maybeSingle();
    const existing = (data?.settings as Record<string, unknown> | null) ?? {};
    await supabase.from('venues').update({ settings: { ...existing, notepad: text } }).eq('id', venueId);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }, [venueId]);

  return { note, setNote, save, saving, saved };
}

export default function NotesPanel({ venueId }: { venueId: string }) {
  const c = useAppColors();
  const { note, setNote, save, saving, saved } = useNotepad(venueId);
  return (
    <div className="rounded-2xl border border-cream-line bg-warm-gray p-5 space-y-3 flex flex-col h-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-cream">
          <StickyNote size={14} className="text-amber-400" /> Notes
        </div>
        {saving ? (
          <span className="text-[10px] text-cream-muted">Saving...</span>
        ) : saved ? (
          <span className="text-[10px] text-leaf-text">✓ Saved</span>
        ) : null}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => save(note)}
        placeholder="Quick notes for today — briefing reminders, staff updates, anything..."
        rows={6}
        className="flex-1 w-full rounded-lg px-3 py-2.5 text-xs outline-none focus:border-leaf-400/30 resize-none transition-colors"
        style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }}
      />
    </div>
  );
}